import DOMPurify from 'dompurify';
import Validate from '@/system/helpers/Validate';
import { reactive } from 'vue';
/**
 * Add plugins to the provided Vue instance, as well as common utilities used in almost all components.
 *
 * @param Vue
 */
export function common(Vue) {
    // Validation
    Vue.use(Validate, { reactive });

    const domPurifyConfig = {
        ADD_TAGS: ['oembed'],
        ADD_ATTR: ['url'],
    }

    DOMPurify.addHook("uponSanitizeElement", (node, data) => {
        // we need to make sure the URL attribute is only used on <oembed> elements
        if (typeof node.hasAttribute === 'function' && node.hasAttribute('url') && data.tagName !== 'oembed') {
            node.parentNode.removeChild(node);
        }
    });

    DOMPurify.addHook('afterSanitizeAttributes', function (node) {
        if ('target' in node) {
            const href = node.getAttribute('href');
            if (href && (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('//') || href.startsWith('www'))) {
                // If the href is not a relative path, add target="_blank"
                node.setAttribute('target', '_blank');
                node.setAttribute('rel', 'noopener');
            }
        }
    });

    Vue.directive('sanitize-html', (el, binding) => {
        el.innerHTML = DOMPurify.sanitize(binding.value, domPurifyConfig)
    })

    Vue.config.globalProperties.sanitizeHtml = (value, config = {}) => {
        return DOMPurify.sanitize(value, {...domPurifyConfig, ...config})
    }

    // Translation
    Vue.config.globalProperties.trans = window.trans;
    Vue.config.globalProperties.transChoice = window.transChoice;

    // Toaster messages
    window.toastr = require('toastr');

    // Alerts
    window.swal = require('sweetalert');

    // Theme (Adminto) specific
    if (window.hasOwnProperty('$')) {
        $(document).ready(() => {
            if ($.Navbar) {
                $.Navbar.init();
            }
        })
    }
}

/**
 * Create an object with loaded global components from `App/components/global/*`, the keys are *.vue file names.
 *
 * @returns Object
 */
export function loadGlobalComponents() {
    var cache = {};
    const importAll = (r) => {
        r.keys().forEach(key => {
            // Clean up the name so it can be directly passed to the Vue instance `components`
            const componentName = key.replace('./','').replace('.vue', '')
            cache[componentName] = r(key).default
        });
    }
    // Import only *.vue files from the global folder, disregard subdirectories
    importAll(require.context('../components/global', false, /\.vue$/));
    importAll(require.context('../components/menu', false, /\.vue$/));

    importAll(require.context('../components/global/messages/global', false, /\.vue$/));

    return cache;
}

/**
 * Set Vue compiler options that are shared between all Vue instances.
 */
export function setSharedVueCompilerOptions(app) {
    app.config.compilerOptions.whitespace = 'preserve'; // or 'condense'

    /*
     * Parts of pages are rendered using Laravel Blade, and the Vue instance is mounted on the root element, and the
     * page is re-parsed by Vue. The issue with this is that if we have `{{ $text }}` in a Blade template, and
     * `$text = '{{ 0[a='constructor'][a]('alert(origin)')() }}'`, then the HTML returned will contain
     * `{{ 0[a='constructor'][a]('alert(origin)')() }}` and Vue will interpret that and we have an XSS issue. This does
     * not affect pre-compiled templates! It only affects the content that is outputted on the server side, which is
     * inside the root node on which the Vue app will be mounted. The quick fix is to use `v-pre`, but that is
     * something we need to remember, so I would like to turn off the template interpolation completely so we don't
     * have to worry about this and we get rid of this type of XSS issue once and for all.
     *
     * Below is a simple example.
     *
     * test.blade.php:
     * ```
     * <div id="app">{{ $text }}<div>
     * ```
     *
     * Controller.php:
     * ```
     * function a() {
     *   return view('test', [
     *    'text' => '{{ 0[a='constructor'][a]('alert(origin)')() }}',
     *   ]);
     * }
     * ```
     *
     * `index.js`:
     * ```
     * createApp().mount('#app');
     * ```
     */
    app.config.compilerOptions.delimiters = ['', ''];
}
